// Subscription Service for Form Builder App
// Handles PayFast subscription management and Firebase user plan tracking

import { ref, update, get } from 'firebase/database';
import { realTimeDb } from '../firebase';
import { PLAN_CONFIG, DEFAULT_PLAN, getPlanLimit } from '../config/planConfig';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  'https://api-ebz57wajla-uc.a.run.app';

/**
 * Subscription service class for managing user plans and PayFast integration
 */
export class SubscriptionService {
  
  /**
   * Initialize or upgrade a user's subscription plan
   * @param {string} userId - Firebase user ID
   * @param {string} email - User email
   * @param {string} targetPlan - Target plan key (TIER_1, TIER_2, etc.)
   * @param {Object} paymentConfig - PayFast configuration
   * @returns {Promise<Object>} - Response from PayFast or success message
   */
  static async initiatePlanUpgrade(userId, email, targetPlan, paymentConfig = {}) {
    if (!userId || !targetPlan || !PLAN_CONFIG[targetPlan]) {
      throw new Error('Invalid parameters for plan upgrade');
    }

    const planMeta = PLAN_CONFIG[targetPlan];
    
    // Handle free plan "upgrade" (downgrade)
    if (planMeta.priceMonthly === 0) {
      await this.updateUserPlan(userId, {
        plan: targetPlan,
        monthlyLimit: planMeta.limit,
        billingStatus: 'active',
        subscriptionId: null,
        downgradedAt: new Date().toISOString()
      });
      return { success: true, message: 'Plan updated to Free tier' };
    }

    // Create PayFast subscription for paid plans
    const response = await fetch(`${API_BASE_URL}/payfast/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plan: targetPlan,
        userId,
        email,
        returnUrl: `${window.location.origin}/dashboard`,
        cancelUrl: `${window.location.origin}/dashboard`,
        ...paymentConfig
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`PayFast initiation failed: ${errorText}`);
    }

    return response;
  }

  /**
   * Cancel a user's subscription
   * @param {string} userId - Firebase user ID
   * @param {string} subscriptionId - PayFast subscription ID
   * @param {string} reason - Cancellation reason
   * @returns {Promise<Object>} - Cancellation result
   */
  static async cancelSubscription(userId, subscriptionId, reason = 'User requested cancellation') {
    if (!userId || !subscriptionId) {
      throw new Error('User ID and subscription ID are required');
    }

    const response = await fetch(`${API_BASE_URL}/payfast/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, subscriptionId, reason })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Cancellation failed: ${errorText}`);
    }

    const result = await response.json();
    
    // Update local Firebase status
    if (result.success) {
      await this.updateUserPlan(userId, {
        billingStatus: 'cancelled',
        subscriptionCancelledAt: new Date().toISOString()
      });
    }

    return result;
  }

  /**
   * Check subscription status with PayFast
   * @param {string} userId - Firebase user ID  
   * @param {string} subscriptionId - PayFast subscription ID
   * @returns {Promise<Object>} - Status check result
   */
  static async checkSubscriptionStatus(userId, subscriptionId) {
    if (!userId || !subscriptionId) {
      throw new Error('User ID and subscription ID are required');
    }

    const response = await fetch(`${API_BASE_URL}/payfast/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, subscriptionId })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Status check failed: ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Update user plan data in Firebase
   * @param {string} userId - Firebase user ID
   * @param {Object} updates - Plan data to update
   * @returns {Promise<void>}
   */
  static async updateUserPlan(userId, updates) {
    const userRef = ref(realTimeDb, `users/${userId}`);
    
    // Add timestamp to all updates
    const timestampedUpdates = {
      ...updates,
      lastUpdated: new Date().toISOString()
    };

    await update(userRef, timestampedUpdates);
  }

  /**
   * Get user's current plan data from Firebase
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object>} - User plan data
   */
  static async getUserPlan(userId) {
    const userRef = ref(realTimeDb, `users/${userId}`);
    const snapshot = await get(userRef);
    
    if (!snapshot.exists()) {
      return null;
    }

    const data = snapshot.val();
    const currentMonth = new Date().toISOString().substring(0, 7);
    
    return {
      plan: data.plan || DEFAULT_PLAN,
      monthlyLimit: data.monthlyLimit || getPlanLimit(data.plan || DEFAULT_PLAN),
      usageCount: data.usage?.count || 0,
      usageMonth: data.usage?.month || currentMonth,
      billingStatus: data.billingStatus || 'active',
      subscriptionId: data.subscriptionId || data.billing?.payfast?.subscriptionId || null,
      lastPaymentDate: data.lastPaymentDate || null,
      lastPaymentAmount: data.lastPaymentAmount || null,
      subscriptionCancelledAt: data.subscriptionCancelledAt || null
    };
  }

  /**
   * Initialize a new user with default free plan
   * @param {string} userId - Firebase user ID
   * @param {string} email - User email
   * @returns {Promise<void>}
   */
  static async initializeNewUser(userId, email) {
    const currentMonth = new Date().toISOString().substring(0, 7);
    const defaultLimit = getPlanLimit(DEFAULT_PLAN);
    
    const defaultProfile = {
      email: email || '',
      plan: DEFAULT_PLAN,
      monthlyLimit: defaultLimit,
      usage: {
        month: currentMonth,
        count: 0
      },
      billingStatus: 'active',
      subscriptionId: null,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    };

    const userRef = ref(realTimeDb, `users/${userId}`);
    await update(userRef, defaultProfile);
    
    return defaultProfile;
  }

  /**
   * Track usage increment for a user
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object>} - Updated usage data
   */
  static async incrementUsage(userId) {
    const userPlan = await this.getUserPlan(userId);
    if (!userPlan) {
      throw new Error('User plan not found');
    }

    const currentMonth = new Date().toISOString().substring(0, 7);
    let newCount = userPlan.usageCount;

    // Reset count if it's a new month
    if (userPlan.usageMonth !== currentMonth) {
      newCount = 0;
    }

    newCount += 1;

    await this.updateUserPlan(userId, {
      'usage/count': newCount,
      'usage/month': currentMonth
    });

    return {
      usageCount: newCount,
      usageMonth: currentMonth,
      monthlyLimit: userPlan.monthlyLimit
    };
  }

  /**
   * Check if user has reached their usage limit
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object>} - Limit check result
   */
  static async checkUsageLimit(userId) {
    const userPlan = await this.getUserPlan(userId);
    if (!userPlan) {
      return { hasLimit: false, reached: false };
    }

    const unlimited = userPlan.monthlyLimit < 0;
    const reached = !unlimited && userPlan.usageCount >= userPlan.monthlyLimit;

    return {
      hasLimit: !unlimited,
      reached,
      usageCount: userPlan.usageCount,
      monthlyLimit: userPlan.monthlyLimit,
      plan: userPlan.plan
    };
  }

  /**
   * Get available upgrade options for a user
   * @param {string} currentPlan - Current plan key
   * @returns {Array} - Array of available upgrade plans
   */
  static getUpgradeOptions(currentPlan) {
    const currentPrice = PLAN_CONFIG[currentPlan]?.priceMonthly || 0;
    
    return Object.keys(PLAN_CONFIG)
      .filter(planKey => {
        const plan = PLAN_CONFIG[planKey];
        return plan.priceMonthly > currentPrice;
      })
      .map(planKey => ({
        key: planKey,
        ...PLAN_CONFIG[planKey]
      }));
  }

  /**
   * Handle PayFast IPN webhook data (for server-side use)
   * @param {Object} ipnData - PayFast IPN payload
   * @returns {Promise<void>}
   */
  static async handlePayFastIPN(ipnData) {
    const {
      payment_status,
      custom_str1: userId,
      custom_str2: planId,
      token: subscriptionId,
      pf_payment_id: paymentId,
      amount_gross: amountPaid
    } = ipnData;

    if (payment_status === 'COMPLETE' && userId && planId) {
      await this.updateUserPlan(userId, {
        plan: planId,
        monthlyLimit: getPlanLimit(planId),
        billingStatus: 'active',
        subscriptionId: subscriptionId || null,
        lastPaymentDate: new Date().toISOString(),
        lastPaymentAmount: parseFloat(amountPaid || 0)
      });
    } else if (payment_status === 'CANCELLED' && subscriptionId) {
      await this.updateUserPlan(userId, {
        billingStatus: 'cancelled',
        subscriptionCancelledAt: new Date().toISOString()
      });
    }
  }
}

export default SubscriptionService;